import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isMidiSupported,
  listDevices,
  parseNoteOff,
  parseNoteOn,
  requestMidiAccess,
  type MidiAccessLike,
  type MidiDeviceInfo,
} from '@/lib/input/midi';

export type MidiStatus = 'unsupported' | 'idle' | 'requesting' | 'connected' | 'denied';

/**
 * Gerencia a conexão com um teclado musical USB/MIDI.
 * Nunca lança erro para a interface: se algo falhar, a aula continua
 * funcionando com o teclado virtual na tela.
 */
export function useMidiInput(
  onNote: (midi: number, velocity: number) => void,
  onNoteOff?: (midi: number) => void,
) {
  const supported = useRef(isMidiSupported()).current;
  const [status, setStatus] = useState<MidiStatus>(supported ? 'idle' : 'unsupported');
  const [devices, setDevices] = useState<MidiDeviceInfo[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastMessageAt, setLastMessageAt] = useState<number>(0);
  const accessRef = useRef<MidiAccessLike | null>(null);
  const handlerRef = useRef(onNote);
  const offHandlerRef = useRef(onNoteOff);

  useEffect(() => {
    handlerRef.current = onNote;
    offHandlerRef.current = onNoteOff;
  }, [onNote, onNoteOff]);

  const attachHandlers = useCallback((access: MidiAccessLike) => {
    access.inputs.forEach((input) => {
      input.onmidimessage = (event: { data: Uint8Array }) => {
        const off = parseNoteOff(event.data);
        if (off !== null) {
          offHandlerRef.current?.(off);
          return;
        }
        const parsed = parseNoteOn(event.data);
        if (parsed) {
          setLastMessageAt(Date.now());
          handlerRef.current(parsed.midi, parsed.velocity);
        }
      };
    });
    setDevices(listDevices(access));
  }, []);

  const connect = useCallback(async () => {
    if (!supported) {
      setStatus('unsupported');
      return;
    }
    setStatus('requesting');
    const access = await requestMidiAccess();
    if (!access) {
      setStatus('denied');
      return;
    }
    accessRef.current = access;
    access.onstatechange = () => {
      if (accessRef.current) {
        setDevices(listDevices(accessRef.current));
        const hasInputs = accessRef.current.inputs.size > 0;
        setStatus(hasInputs ? 'connected' : 'denied');
      }
    };
    attachHandlers(access);
    const found = listDevices(access);
    setDevices(found);
    setSelectedId((current) => current ?? found[0]?.id ?? null);
    setStatus(found.length > 0 ? 'connected' : 'denied');
  }, [attachHandlers, supported]);

  const disconnect = useCallback(() => {
    const access = accessRef.current;
    if (access) {
      access.inputs.forEach((input) => {
        input.onmidimessage = null;
      });
      access.onstatechange = null;
    }
    accessRef.current = null;
    setDevices([]);
    setSelectedId(null);
    setStatus(supported ? 'idle' : 'unsupported');
  }, [supported]);

  useEffect(() => () => {
    const access = accessRef.current;
    if (access) {
      access.inputs.forEach((input) => {
        input.onmidimessage = null;
      });
    }
  }, []);

  return {
    supported,
    status,
    devices,
    selectedId,
    setSelectedId,
    lastMessageAt,
    connect,
    disconnect,
  };
}